import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateVendingSlotInput,
  createVendingSlotSchema,
  type KioskDevice,
  type MachineIssue,
  queryKeys,
  type RestockSlotInput,
  restockSlotSchema,
  type Unit,
  type UpdateVendingSlotInput,
  updateVendingSlotSchema,
  type VendAttempt,
  type VendableProduct,
  type VendingMachine,
  type VendingSlot,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import {
  AlertTriangle,
  Lock,
  LockOpen,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  ShieldOff,
  Trash2,
} from 'lucide-react'
import type * as React from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Page, PageHeader } from '../components/app-shell.js'
import { MachineCamerasTab } from '../components/machine-cameras-tab.js'
import { StartScreenCard } from '../components/machine-start-screen-card.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { useReleaseManifest } from '../lib/release-manifest.js'
import { CabinetSettingsTab, PickupsTab } from './machine-cabinet.js'

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`
  return `${Math.floor(seconds / 86_400)} days ago`
}

/** A "real" slot code is a row letter followed by a column number (A1, B12,
 *  optionally "A-1" / "A 1"), matching how the machine itself is labeled.
 *  Codes are otherwise free text (see `upsertVendingSlotSchema`), so this can
 *  fail to match — callers fall back to an unordered "Other slots" group. */
function parseSlotCode(code: string): { row: string; col: number } | null {
  const match = /^([a-z]+)[\s-]*(\d+)$/i.exec(code.trim())
  const [, rowPart, colPart] = match ?? []
  if (!rowPart || !colPart) return null
  const col = Number(colPart)
  if (!Number.isFinite(col) || col > 50) return null
  return { row: rowPart.toUpperCase(), col }
}

interface SlotPlanogram {
  rows: { label: string; cells: { col: number; slot: VendingSlot }[] }[]
  minCol: number
  maxCol: number
  other: VendingSlot[]
}

/** Lays slots out the way they sit in the machine: one row per row-letter,
 *  columns in numeric order left to right, gaps where a column is unused. */
function groupSlots(slots: VendingSlot[]): SlotPlanogram {
  const byRow = new Map<string, Map<number, VendingSlot>>()
  const other: VendingSlot[] = []
  let minCol = Infinity
  let maxCol = -Infinity

  for (const slot of slots) {
    const parsed = parseSlotCode(slot.code)
    if (!parsed) {
      other.push(slot)
      continue
    }
    const cols = byRow.get(parsed.row) ?? new Map<number, VendingSlot>()
    if (cols.has(parsed.col)) {
      // Two codes parsed to the same coordinate (e.g. "A1" and "A01") — keep
      // the first and don't drop the second silently, just don't overlap it.
      other.push(slot)
      continue
    }
    cols.set(parsed.col, slot)
    byRow.set(parsed.row, cols)
    minCol = Math.min(minCol, parsed.col)
    maxCol = Math.max(maxCol, parsed.col)
  }

  const rows = [...byRow.entries()]
    .sort(([a], [b]) => a.length - b.length || a.localeCompare(b))
    .map(([label, cols]) => ({
      label,
      cells: [...cols.entries()].sort(([a], [b]) => a - b).map(([col, slot]) => ({ col, slot })),
    }))

  return {
    rows,
    minCol: Number.isFinite(minCol) ? minCol : 0,
    maxCol: Number.isFinite(maxCol) ? maxCol : 0,
    other,
  }
}

function SlotTile({
  slot,
  onOpen,
  style,
  className,
}: {
  slot: VendingSlot
  onOpen?: () => void
  style?: React.CSSProperties
  className?: string
}) {
  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-identifier text-sm font-medium">{slot.code}</span>
        {slot.productType ? (
          <span
            className={`font-identifier text-xs tabular ${
              slot.qty === 0 ? 'text-danger' : 'text-foreground-muted'
            }`}
          >
            {slot.qty}/{slot.capacity}
          </span>
        ) : null}
      </div>
      <p
        className={`truncate text-2xs ${
          slot.productName ? 'text-foreground-muted' : 'italic text-foreground-subtle'
        }`}
      >
        {slot.productName ?? 'No product'}
      </p>
      {!slot.enabled ? <p className="text-2xs text-danger">Disabled</p> : null}
    </>
  )
  const sharedClassName = `rounded-md border border-border bg-surface px-2.5 py-2 text-start shadow-xs ${
    !slot.enabled ? 'opacity-60' : ''
  } ${className ?? ''}`

  if (!onOpen) {
    return (
      <div className={sharedClassName} style={style}>
        {inner}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      style={style}
      className={`${sharedClassName} cursor-pointer transition-colors hover:bg-surface-sunken`}
    >
      {inner}
    </button>
  )
}

function RestockDialog({
  machineId,
  slot,
  manage,
  onClose,
}: {
  machineId: string
  slot: VendingSlot
  manage: SlotManageActions | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RestockSlotInput>({
    resolver: zodResolver(restockSlotSchema),
    defaultValues: { setQty: slot.qty },
  })

  const restock = useMutation({
    mutationFn: (input: RestockSlotInput) =>
      api.post(`/vending/machines/${machineId}/slots/${slot.id}/restock`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      toast.success('Slot updated')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not restock', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <form onSubmit={handleSubmit((values) => restock.mutate(values))}>
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 pe-8">
              <DialogTitle>Restock slot {slot.code}</DialogTitle>
              {manage ? <SlotManageMenu slot={slot} actions={manage} /> : null}
            </div>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Field label="Set the count on the shelf to" error={errors.setQty?.message}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={slot.capacity}
                  {...register('setQty', { valueAsNumber: true })}
                />
              )}
            </Field>
            <p className="text-xs text-foreground-muted">Capacity is {slot.capacity}.</p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={restock.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Edit / enable-disable / delete, reachable from any dialog opened on an
 *  existing slot — the fix for t0593's third symptom (no way to manage a
 *  slot once created). Kept as one small menu shared by all three "open a
 *  slot" dialogs rather than duplicating the three actions in each. */
interface SlotManageActions {
  onEdit: (slot: VendingSlot) => void
  onToggleEnabled: (slot: VendingSlot) => void
  onDelete: (slot: VendingSlot) => void
  togglePending: boolean
}

function SlotManageMenu({ slot, actions }: { slot: VendingSlot; actions: SlotManageActions }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Manage slot ${slot.code}`}>
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => actions.onEdit(slot)}>
          <Pencil /> Edit slot
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={actions.togglePending}
          onSelect={() => actions.onToggleEnabled(slot)}
        >
          <Power /> {slot.enabled ? 'Disable slot' : 'Enable slot'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => actions.onDelete(slot)}>
          <Trash2 /> Delete slot
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Everything for one serialized slot: the loaded IMEIs (with unload, for
 *  writers), and a way to load another. Read-only users still see the IMEIs
 *  here — that's the only place this information lives once the grid tiles
 *  are too small to show it inline. */
function SerializedSlotDialog({
  machineId,
  slot,
  locationId,
  canWrite,
  manage,
  onClose,
}: {
  machineId: string
  slot: VendingSlot
  locationId: string
  canWrite: boolean
  manage: SlotManageActions | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [imei, setImei] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [unloadingImei, setUnloadingImei] = useState<string | null>(null)
  const canSubmit = /^\d{15}$/.test(imei)

  // Debounced so every keystroke (or the tail end of a barcode scan) doesn't
  // fire its own request — matches the command palette's search pattern.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(imei), 200)
    return () => clearTimeout(timer)
  }, [imei])

  const unitFilters = {
    productId: slot.productId,
    status: 'sellable' as const,
    unassigned: true,
    q: debouncedQ || undefined,
  }
  const { data: candidates, isFetching: searching } = useQuery({
    queryKey: queryKeys.units.list(unitFilters),
    queryFn: () =>
      api.get<{ items: Unit[] }>('/units', {
        query: { ...unitFilters, sort: 'imei', order: 'asc', pageSize: 50 },
      }),
    enabled: canWrite && slot.productId !== null,
  })
  const candidateUnits = candidates?.items ?? []

  const load = useMutation({
    mutationFn: () =>
      api.post(`/vending/machines/${machineId}/slots/${slot.id}/load-unit`, { imei }),
    onSuccess: () => {
      const picked = candidateUnits.find((u) => u.imei === imei)
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(
        picked && picked.locationId !== locationId
          ? `Device loaded — transferred here from ${picked.locationName ?? 'another location'}`
          : 'Device loaded',
      )
      setImei('')
    },
    onError: (error) =>
      toast.error(
        'Could not load that device',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const unload = useMutation({
    mutationFn: (unloadImei: string) =>
      api.post(`/vending/machines/${machineId}/slots/${slot.id}/unload-unit`, {
        imei: unloadImei,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Device unloaded')
    },
    onError: (error) =>
      toast.error('Could not unload', error instanceof ApiError ? error.message : undefined),
    onSettled: () => setUnloadingImei(null),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) load.mutate()
          }}
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 pe-8">
              <DialogTitle>
                Slot {slot.code}
                {slot.productName ? ` — ${slot.productName}` : ''}
              </DialogTitle>
              {manage ? <SlotManageMenu slot={slot} actions={manage} /> : null}
            </div>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="divide-y divide-border">
              {slot.loadedUnits.length === 0 ? (
                <p className="py-1 text-sm text-foreground-muted">Nothing loaded</p>
              ) : null}
              {slot.loadedUnits.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="font-identifier">{u.imei}</span>
                  {canWrite ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      loading={unload.isPending && unloadingImei === u.imei}
                      onClick={() => {
                        setUnloadingImei(u.imei)
                        unload.mutate(u.imei)
                      }}
                    >
                      Unload
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
            {canWrite ? (
              <>
                <Field
                  label="IMEI"
                  hint="Scan the IMEI, or type a few digits to find a device in stock at any location."
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={imei}
                      onChange={(e) => setImei(e.target.value.replace(/\D/g, ''))}
                      maxLength={15}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  )}
                </Field>
                <div className="max-h-40 overflow-y-auto rounded-md border border-border">
                  {searching ? (
                    <p className="p-2 text-sm text-foreground-muted">Searching…</p>
                  ) : candidateUnits.length === 0 ? (
                    <p className="p-2 text-sm text-foreground-muted">
                      {debouncedQ.length === 15
                        ? 'No sellable device with this IMEI is in stock at any location.'
                        : debouncedQ
                          ? 'No match — you can still scan or type the full 15-digit IMEI.'
                          : 'No devices of this model in stock at any location.'}
                    </p>
                  ) : (
                    candidateUnits.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setImei(u.imei)}
                        className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-start text-sm hover:bg-surface-sunken ${
                          u.imei === imei ? 'bg-surface-sunken' : ''
                        }`}
                      >
                        <span className="font-identifier">{u.imei}</span>
                        <span className="shrink-0 text-xs text-foreground-muted">
                          {u.locationId === locationId
                            ? u.locationName
                            : `${u.locationName ?? 'elsewhere'} — will be moved here`}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canWrite ? (
              <Button
                type="submit"
                variant="primary"
                loading={load.isPending}
                disabled={!canSubmit}
              >
                Load
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const PRODUCT_SELECT_CLASS = 'h-8 w-full rounded-md border border-border bg-surface px-2 text-sm'

type AddSlotFormValues = z.input<typeof createVendingSlotSchema>
type EditSlotFormValues = z.input<typeof updateVendingSlotSchema>

/** Create-only — a code already used on this machine comes back as a 409 from
 *  the field-agnostic error toast below, never a silent overwrite (t0593). */
function AddSlotDialog({ machineId, onClose }: { machineId: string; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { data: products } = useQuery({
    queryKey: queryKeys.vending.products(),
    queryFn: () => api.get<VendableProduct[]>('/vending/products'),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddSlotFormValues, unknown, CreateVendingSlotInput>({
    resolver: zodResolver(createVendingSlotSchema),
    defaultValues: { code: '', productId: null, capacity: 10, enabled: true },
  })

  const createSlot = useMutation({
    mutationFn: (input: CreateVendingSlotInput) =>
      api.post<VendingSlot>(`/vending/machines/${machineId}/slots`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      toast.success('Slot added')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not add the slot', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <form onSubmit={handleSubmit((values) => createSlot.mutate(values))}>
          <DialogHeader>
            <DialogTitle>Add a slot</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Field label="Slot code" error={errors.code?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('code')}
                  placeholder="A1"
                  className="uppercase"
                  autoCapitalize="characters"
                />
              )}
            </Field>
            <Field label="Product">
              {(props) => (
                <select
                  {...props}
                  {...register('productId', { setValueAs: (v: string) => (v ? v : null) })}
                  className={PRODUCT_SELECT_CLASS}
                >
                  <option value="">No product</option>
                  {products?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.sku}) — {p.availableCount} in stock
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Capacity" error={errors.capacity?.message}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  {...register('capacity', { valueAsNumber: true })}
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={createSlot.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Edits an existing slot's product, capacity or enabled state — the fix for
 *  t0593's second symptom (re-adding a slot's code was previously the only
 *  way to "edit" it, and that silently destroyed it instead). The slot code
 *  itself never changes here; delete and re-add for that. */
function EditSlotDialog({
  machineId,
  slot,
  onClose,
}: {
  machineId: string
  slot: VendingSlot
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { data: products } = useQuery({
    queryKey: queryKeys.vending.products(),
    queryFn: () => api.get<VendableProduct[]>('/vending/products'),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditSlotFormValues, unknown, UpdateVendingSlotInput>({
    resolver: zodResolver(updateVendingSlotSchema),
    defaultValues: { productId: slot.productId, capacity: slot.capacity, enabled: slot.enabled },
  })

  const updateSlot = useMutation({
    mutationFn: (patch: UpdateVendingSlotInput) =>
      api.patch<VendingSlot>(`/vending/machines/${machineId}/slots/${slot.id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      toast.success('Slot saved')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save the slot', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        {products ? (
          <form onSubmit={handleSubmit((values) => updateSlot.mutate(values))}>
            <DialogHeader>
              <DialogTitle>Edit slot {slot.code}</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <Field label="Product">
                {(props) => (
                  <select
                    {...props}
                    defaultValue={slot.productId ?? ''}
                    {...register('productId', { setValueAs: (v: string) => (v ? v : null) })}
                    className={PRODUCT_SELECT_CLASS}
                  >
                    <option value="">No product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.sku}) — {p.availableCount} in stock
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Capacity" error={errors.capacity?.message}>
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={1}
                    {...register('capacity', { valueAsNumber: true })}
                  />
                )}
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={updateSlot.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Edit slot {slot.code}</DialogTitle>
            </DialogHeader>
            {/* Product options load async — the select mounts once they're in,
             *  so it can seed on the slot's current product (t0602). Rendering
             *  it earlier with only the "No product" option means the browser
             *  falls back to that option and never recovers when options
             *  arrive, since nothing re-applies the value after mount. */}
            <DialogBody className="h-32" />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SlotsTab({ machineId, locationId }: { machineId: string; locationId: string }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('inventory:write')
  const canManage = can('kiosks:manage')
  // Slots are looked up by id, not snapshotted, so an open dialog always
  // reflects the latest fetch (e.g. a load/unload updates the list live
  // instead of going stale until the dialog is closed and reopened).
  const [openSlotId, setOpenSlotId] = useState<string | null>(null)
  const [addingSlot, setAddingSlot] = useState(false)
  const [editingSlot, setEditingSlot] = useState<VendingSlot | null>(null)
  const [deletingSlot, setDeletingSlot] = useState<VendingSlot | null>(null)

  const { data: slots, isLoading } = useQuery({
    queryKey: queryKeys.vending.slots(machineId),
    queryFn: () => api.get<VendingSlot[]>(`/vending/machines/${machineId}/slots`),
  })

  const openSlot = slots?.find((s) => s.id === openSlotId) ?? null
  const planogram = useMemo(() => groupSlots(slots ?? []), [slots])

  const toggleEnabled = useMutation({
    mutationFn: (slot: VendingSlot) =>
      api.patch<VendingSlot>(`/vending/machines/${machineId}/slots/${slot.id}`, {
        enabled: !slot.enabled,
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      toast.success(updated.enabled ? 'Slot enabled' : 'Slot disabled')
    },
    onError: (error) =>
      toast.error(
        'Could not update the slot',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const deleteSlot = useMutation({
    mutationFn: (slotId: string) => api.delete(`/vending/machines/${machineId}/slots/${slotId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.slots(machineId) })
      toast.success('Slot deleted')
      setDeletingSlot(null)
    },
    onError: (error) =>
      toast.error(
        'Could not delete the slot',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const manageActions: SlotManageActions | null = canManage
    ? {
        onEdit: (slot) => {
          setOpenSlotId(null)
          setEditingSlot(slot)
        },
        onToggleEnabled: (slot) => toggleEnabled.mutate(slot),
        onDelete: (slot) => {
          setOpenSlotId(null)
          setDeletingSlot(slot)
        },
        togglePending: toggleEnabled.isPending,
      }
    : null

  function canOpenSlot(slot: VendingSlot): boolean {
    if (slot.productType === 'serialized') {
      // The read surface for loaded IMEIs — open for everyone; the dialog
      // itself hides the write affordances.
      return true
    }
    if (slot.productType === 'stock') return canWrite || canManage
    // No product assigned yet — opening it means assigning one, which is a
    // configuration change, so it takes the same permission as editing.
    return canManage
  }

  function openSlotDialog(slot: VendingSlot) {
    if (slot.productType === 'serialized' || slot.productType === 'stock') {
      setOpenSlotId(slot.id)
      return
    }
    setEditingSlot(slot)
  }

  if (isLoading)
    return (
      <Card>
        <CardContent className="h-40 pt-4" />
      </Card>
    )

  return (
    <div className="space-y-3">
      {canManage ? (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={() => setAddingSlot(true)}>
            <Plus /> Add slot
          </Button>
        </div>
      ) : null}

      {slots?.length === 0 ? (
        <Card>
          <EmptyState
            title="No slots configured"
            description="Add a slot to start loading or restocking."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {planogram.rows.length > 0 ? (
            <div className="-m-1 overflow-x-auto p-1 pb-2">
              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns: `max-content repeat(${
                    planogram.maxCol - planogram.minCol + 1
                  }, minmax(7rem, 1fr))`,
                }}
              >
                {planogram.rows.map((row, rowIndex) => (
                  <Fragment key={row.label}>
                    <div
                      className="flex items-center pe-1 font-identifier text-xs font-medium text-foreground-subtle"
                      style={{ gridRowStart: rowIndex + 1, gridColumnStart: 1 }}
                    >
                      {row.label}
                    </div>
                    {row.cells.map(({ col, slot }) => (
                      <SlotTile
                        key={slot.id}
                        slot={slot}
                        onOpen={canOpenSlot(slot) ? () => openSlotDialog(slot) : undefined}
                        style={{
                          gridRowStart: rowIndex + 1,
                          gridColumnStart: col - planogram.minCol + 2,
                        }}
                      />
                    ))}
                  </Fragment>
                ))}
              </div>
            </div>
          ) : null}

          {planogram.other.length > 0 ? (
            <div className="space-y-1.5">
              {planogram.rows.length > 0 ? (
                <p className="text-xs text-foreground-subtle">Other slots</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {planogram.other.map((slot) => (
                  <SlotTile
                    key={slot.id}
                    slot={slot}
                    className="min-w-28"
                    onOpen={canOpenSlot(slot) ? () => openSlotDialog(slot) : undefined}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {openSlot?.productType === 'stock' ? (
        <RestockDialog
          machineId={machineId}
          slot={openSlot}
          manage={manageActions}
          onClose={() => setOpenSlotId(null)}
        />
      ) : null}
      {openSlot?.productType === 'serialized' ? (
        <SerializedSlotDialog
          machineId={machineId}
          slot={openSlot}
          locationId={locationId}
          canWrite={canWrite}
          manage={manageActions}
          onClose={() => setOpenSlotId(null)}
        />
      ) : null}

      {addingSlot ? (
        <AddSlotDialog machineId={machineId} onClose={() => setAddingSlot(false)} />
      ) : null}
      {editingSlot ? (
        <EditSlotDialog
          machineId={machineId}
          slot={editingSlot}
          onClose={() => setEditingSlot(null)}
        />
      ) : null}

      <Dialog open={deletingSlot !== null} onOpenChange={(open) => !open && setDeletingSlot(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete slot {deletingSlot?.code}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              This only works while the slot is empty — restock it to 0 or unload its device first
              if it still holds stock. A slot with past vend history can't be deleted at all;
              disable it instead.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeletingSlot(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteSlot.isPending}
              onClick={() => deletingSlot && deleteSlot.mutate(deletingSlot.id)}
            >
              Delete slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function IssuesTab({ machineId }: { machineId: string }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [resolving, setResolving] = useState<MachineIssue | null>(null)
  const [note, setNote] = useState('')

  const { data: issues, isLoading } = useQuery({
    queryKey: queryKeys.vending.issues({ machineId }),
    queryFn: () => api.get<MachineIssue[]>('/vending/issues', { query: { machineId } }),
    refetchInterval: 20_000,
  })

  const resolve = useMutation({
    mutationFn: (input: { id: string; resolutionNote: string | null }) =>
      api.post(`/vending/issues/${input.id}/resolve`, { resolutionNote: input.resolutionNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.issues({ machineId }) })
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machines() })
      toast.success('Issue resolved')
      setResolving(null)
      setNote('')
    },
    onError: (error) =>
      toast.error('Could not resolve', error instanceof ApiError ? error.message : undefined),
  })

  if (isLoading)
    return (
      <Card>
        <CardContent className="h-40 pt-4" />
      </Card>
    )
  if (issues?.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={AlertTriangle}
          title="No issues"
          description="Nothing has gone wrong here."
        />
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {issues?.map((issue) => (
        <Card key={issue.id}>
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Badge tone={issue.status === 'open' ? 'danger' : 'success'}>
                    {issue.status}
                  </Badge>
                  {issue.kind === 'customer_reported' ? (
                    <Badge tone="info">Customer report</Badge>
                  ) : null}
                  <span className="text-sm font-medium">{issue.summary}</span>
                </div>
                {issue.orderNumber ? (
                  <p className="mt-1 text-xs text-foreground-muted">
                    Order {issue.orderNumber} · {issue.customerName ?? 'no account'} ·{' '}
                    {issue.customerPhone ?? 'no phone on file'}
                    {issue.imei ? ` · ${issue.imei}` : ''}
                  </p>
                ) : null}
                {issue.reporterName || issue.reporterPhone ? (
                  <p className="mt-1 text-xs text-foreground-muted">
                    Reported by {issue.reporterName ?? 'anonymous'} ·{' '}
                    {issue.reporterPhone ?? 'no callback number'}
                  </p>
                ) : null}
                <p className="mt-1 text-xs text-foreground-subtle">
                  {relativeTime(issue.createdAt)}
                </p>
                {issue.resolutionNote ? (
                  <p className="mt-2 text-sm text-foreground-muted">
                    Resolved: {issue.resolutionNote}
                  </p>
                ) : null}
              </div>
              {issue.status === 'open' ? (
                <Button variant="secondary" size="sm" onClick={() => setResolving(issue)}>
                  Mark resolved
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={resolving !== null} onOpenChange={(open) => !open && setResolving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve issue</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Field label="Resolution note" hint="What happened, and how it was handled.">
              {(props) => (
                <Input {...props} value={note} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setResolving(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={resolve.isPending}
              onClick={() =>
                resolving && resolve.mutate({ id: resolving.id, resolutionNote: note || null })
              }
            >
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function VendsTab({ machineId }: { machineId: string }) {
  const { data: attempts, isLoading } = useQuery({
    queryKey: ['vending', 'vends', machineId],
    queryFn: () => api.get<VendAttempt[]>(`/vending/machines/${machineId}/vends`),
  })

  if (isLoading)
    return (
      <Card>
        <CardContent className="h-40 pt-4" />
      </Card>
    )
  if (attempts?.length === 0) {
    return (
      <Card>
        <EmptyState title="No vend attempts yet" description="Nothing has been dispensed here." />
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="divide-y divide-border pt-4">
        {attempts?.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <div>
              <span className="font-identifier">{a.orderNumber}</span>
              {a.imei ? (
                <span className="ms-2 font-identifier text-foreground-muted">{a.imei}</span>
              ) : null}
              {a.slotCode ? (
                <span className="ms-2 text-foreground-muted">slot {a.slotCode}</span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Badge
                tone={
                  a.status === 'dispensed'
                    ? 'success'
                    : a.status === 'failed' || a.status === 'timed_out'
                      ? 'danger'
                      : 'neutral'
                }
              >
                {a.status}
              </Badge>
              <span className="text-xs text-foreground-subtle">{relativeTime(a.createdAt)}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function DevicesTab({ machine }: { machine: VendingMachine }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [pairing, setPairing] = useState<{ code: string; deviceName: string } | null>(null)
  const [revoking, setRevoking] = useState<KioskDevice | null>(null)
  const [unlocking, setUnlocking] = useState<KioskDevice | null>(null)
  const canManage = can('kiosks:manage')

  // The two device cards need more than the machine payload carries (status,
  // last seen, agent version, last error) — the fleet endpoint already has
  // it, and the dashboard already polls this exact key, so this just shares
  // that cache rather than opening a second source of truth.
  const { data: kiosks } = useQuery({
    queryKey: queryKeys.kiosks.list(),
    queryFn: () => api.get<KioskDevice[]>('/kiosks'),
    // t1168: the tablet reports its lock state over the live connection, so
    // poll faster here than the dashboard does to keep the card near-live.
    refetchInterval: 5_000,
  })
  const { data: manifest } = useReleaseManifest()

  const pair = useMutation({
    mutationFn: (deviceId: string) =>
      api.post<{ pairingCode: string; expiresAt: string }>(`/kiosks/${deviceId}/pairing-code`, {}),
    onSuccess: (result, deviceId) =>
      setPairing({
        code: result.pairingCode,
        deviceName: deviceId === machine.tabletDeviceId ? 'tablet' : 'Pi',
      }),
    onError: (error) =>
      toast.error('Could not create a code', error instanceof ApiError ? error.message : undefined),
  })

  const sendCommand = useMutation({
    mutationFn: (input: { id: string; action: string }) =>
      api.post<{ delivered: boolean }>(`/kiosks/${input.id}/command`, {
        action: input.action,
        params: {},
      }),
    onSuccess: (result) => {
      if (result.delivered) {
        toast.success('Command sent')
        // The tablet answers over its socket within a moment; pick up the
        // new lock state without waiting for the next poll.
        setTimeout(() => queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() }), 1_500)
      } else
        toast.toast({
          tone: 'warning',
          title: 'Kiosk has no live connection',
          description: 'It is reporting in, but not holding a connection commands can travel over.',
        })
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/kiosks/${id}/revoke`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machine(machine.id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machines() })
      toast.success('Kiosk revoked', 'It has been disconnected and can no longer sign in.')
      setRevoking(null)
    },
    onError: (error) =>
      toast.error('Could not revoke', error instanceof ApiError ? error.message : undefined),
  })

  // t0660: this records what build a tablet should install. t1168: a tablet
  // holding its live connection is nudged to check in right away; one that
  // isn't picks the request up on its next check-in (within about a minute).
  const requestUpdate = useMutation({
    mutationFn: (input: { id: string; versionCode: number }) =>
      api.post<{ ok: true }>(`/kiosks/${input.id}/request-update`, {
        versionCode: input.versionCode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() })
      toast.success(
        'Update requested',
        'A connected tablet starts right away; otherwise on its next check-in.',
      )
    },
    onError: (error) =>
      toast.error(
        'Could not request the update',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const cancelUpdate = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/kiosks/${id}/request-update`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() })
      toast.success('Update request cancelled')
    },
    onError: (error) =>
      toast.error(
        'Could not cancel the request',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <StartScreenCard machine={machine} />
      {(machine.kind === 'cabinet'
        ? (['tabletDeviceId'] as const)
        : (['tabletDeviceId', 'piDeviceId'] as const)
      ).map((key) => {
        const deviceId = machine[key]
        const isTablet = key === 'tabletDeviceId'
        const role = isTablet ? 'Tablet' : 'Raspberry Pi'
        const device = deviceId ? kiosks?.find((k) => k.id === deviceId) : undefined
        const updateAvailable =
          isTablet &&
          device?.appVersionCode != null &&
          manifest != null &&
          manifest.versionCode > device.appVersionCode
        const lock = isTablet ? device?.tabletStatus?.lock : undefined
        const updatePending =
          isTablet &&
          device?.updateTargetVersionCode != null &&
          device?.appVersionCode != null &&
          device.updateTargetVersionCode > device.appVersionCode

        return (
          <Card key={key}>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">{role}</p>
                  {device ? (
                    <p className="truncate text-xs text-foreground-muted">{device.name}</p>
                  ) : null}
                </div>
                {canManage && deviceId ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Actions for the ${role}`}>
                        <MoreVertical />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => pair.mutate(deviceId)}>
                        <RefreshCw />{' '}
                        {device?.status === 'unpaired' ? 'Pair this kiosk' : 'Re-pair'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => sendCommand.mutate({ id: deviceId, action: 'reload_ui' })}
                        disabled={!device?.connected}
                      >
                        <RefreshCw /> Reload screen
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => sendCommand.mutate({ id: deviceId, action: 'screen_off' })}
                        disabled={!device?.connected}
                      >
                        <Power /> Turn screen off
                      </DropdownMenuItem>
                      {isTablet && lock !== 'locked' ? (
                        <DropdownMenuItem
                          onSelect={() =>
                            sendCommand.mutate({ id: deviceId, action: 'kiosk_lock' })
                          }
                          disabled={!device?.connected}
                        >
                          <Lock /> Lock into kiosk mode
                        </DropdownMenuItem>
                      ) : null}
                      {isTablet && lock !== 'unlocked' && lock !== 'setup_needed' ? (
                        <DropdownMenuItem
                          onSelect={() => device && setUnlocking(device)}
                          disabled={!device?.connected}
                        >
                          <LockOpen /> Unlock for servicing
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        destructive
                        disabled={!device}
                        onSelect={() => device && setRevoking(device)}
                      >
                        <ShieldOff /> Revoke access
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>

              {deviceId ? (
                <>
                  <div className="mt-3 flex items-center gap-2">
                    <span
                      className={`size-2 rounded-full ${
                        device?.connected
                          ? 'bg-success'
                          : device?.online
                            ? 'bg-warning'
                            : device?.status === 'disabled'
                              ? 'bg-danger'
                              : 'bg-neutral-400'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="text-sm">
                      {device?.status === 'disabled'
                        ? 'Disabled'
                        : device?.status === 'unpaired'
                          ? 'Not paired'
                          : device?.connected
                            ? 'Online'
                            : device?.online
                              ? 'Online (no live connection)'
                              : 'Offline'}
                    </span>
                    {device ? (
                      <span className="ms-auto text-xs text-foreground-muted">
                        {relativeTime(device.lastSeenAt)}
                      </span>
                    ) : null}
                  </div>

                  {isTablet && device?.tabletStatus ? (
                    <div className="mt-2 space-y-1 text-xs">
                      {lock ? (
                        <p
                          className={
                            lock === 'locked' ? 'text-success' : 'font-medium text-warning'
                          }
                        >
                          {lock === 'locked'
                            ? device.tabletStatus.deviceOwner
                              ? 'Kiosk lock: on (fully managed)'
                              : 'Kiosk lock: on'
                            : lock === 'unlocked'
                              ? 'Kiosk lock: off — unlocked for servicing'
                              : 'Kiosk lock: needs one-time setup on the tablet'}
                        </p>
                      ) : null}
                      <p className="text-foreground-muted">
                        {device.tabletStatus.batteryPct != null
                          ? `Battery ${device.tabletStatus.batteryPct}%${
                              device.tabletStatus.charging ? ' (charging)' : ''
                            } · `
                          : ''}
                        status {relativeTime(device.tabletStatus.reportedAt)}
                      </p>
                    </div>
                  ) : null}

                  {device?.online && !device.connected ? (
                    <p className="mt-2 text-xs text-foreground-muted">
                      Reporting in, but not holding a live connection — commands can't be delivered
                      right now.
                    </p>
                  ) : null}

                  {device?.agentVersion ? (
                    <p className="mt-2 font-identifier text-2xs text-foreground-subtle">
                      agent {device.agentVersion}
                      {device.appVersionCode != null ? ` (build ${device.appVersionCode})` : ''}
                    </p>
                  ) : null}

                  {updatePending ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge tone="info" className="max-w-full">
                        <span className="truncate">
                          Update requested (build {device?.updateTargetVersionCode}) — waiting for
                          the tablet to pick it up
                        </span>
                      </Badge>
                      {canManage ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deviceId && cancelUpdate.mutate(deviceId)}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </div>
                  ) : updateAvailable ? (
                    <div className="mt-2">
                      {canManage ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            deviceId &&
                            manifest &&
                            requestUpdate.mutate({
                              id: deviceId,
                              versionCode: manifest.versionCode,
                            })
                          }
                        >
                          Install update remotely
                        </Button>
                      ) : (
                        <Badge tone="warning" className="max-w-full">
                          <span className="truncate">
                            Update available: v{manifest?.versionName} — install from the tablet's
                            admin screen
                          </span>
                        </Badge>
                      )}
                      {canManage && device?.deviceOwner === false ? (
                        <p className="mt-1 text-xs text-foreground-muted">
                          This tablet isn't device-owner provisioned — it will download and stage
                          the update, but someone at the machine will still need to confirm the
                          install. Full device-owner management needs a factory reset and on-site
                          setup; it can't be switched on from here.
                        </p>
                      ) : null}
                      {canManage && device?.deviceOwner == null ? (
                        <p className="mt-1 text-xs text-foreground-muted">
                          This tablet's app predates remote install — update it by hand once from
                          its admin screen to enable this going forward.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {device?.lastError ? (
                    <Badge tone="danger" className="mt-2 max-w-full">
                      <span className="truncate">{device.lastError}</span>
                    </Badge>
                  ) : null}
                </>
              ) : (
                <p className="mt-3 text-sm text-foreground-muted">No device linked.</p>
              )}
            </CardContent>
          </Card>
        )
      })}

      <Dialog open={pairing !== null} onOpenChange={(open) => !open && setPairing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pairing code for the {pairing?.deviceName}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="rounded-lg bg-surface-sunken py-4 text-center font-identifier text-3xl font-semibold tracking-widest">
              {pairing?.code}
            </p>
            <Alert tone="info">This code is shown only now and expires in ten minutes.</Alert>
          </DialogBody>
          <DialogFooter>
            <Button variant="primary" onClick={() => setPairing(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unlocking !== null} onOpenChange={(open) => !open && setUnlocking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unlock the {unlocking?.name} for servicing?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              Customers can leave the checkout app until you lock it again here, or the tablet is
              restarted or an admin signs out on it.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setUnlocking(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={sendCommand.isPending}
              onClick={() => {
                if (!unlocking) return
                sendCommand.mutate({ id: unlocking.id, action: 'kiosk_unlock' })
                setUnlocking(null)
              }}
            >
              Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revoking !== null} onOpenChange={(open) => !open && setRevoking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revoke the {revoking?.name}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              The terminal is disconnected immediately and cannot sign in again. Use this if it has
              been lost or stolen. Pairing it again later restores it.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revoking && revoke.mutate(revoking.id)}
            >
              Revoke access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function MachineDetailPage() {
  const { machineId } = useParams({ from: '/authed/machines/$machineId' })

  const { data: machine, isLoading } = useQuery({
    queryKey: queryKeys.vending.machine(machineId),
    queryFn: () => api.get<VendingMachine>(`/vending/machines/${machineId}`),
    refetchInterval: 15_000,
  })

  if (isLoading || !machine) {
    return (
      <Page>
        <Card>
          <CardContent className="h-40 pt-4" />
        </Card>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={machine.name}
        description={
          machine.status === 'retired'
            ? `${machine.locationName} · Retired — its tablet and Pi are revoked; history is kept.`
            : machine.locationName
        }
        actions={machine.status === 'retired' ? <Badge tone="neutral">Retired</Badge> : undefined}
      />

      {machine.kind === 'cabinet' ? (
        <Tabs defaultValue={machine.cabinetCode === null ? 'cabinet' : 'pickups'}>
          <TabsList>
            <TabsTrigger value="pickups">Pickups</TabsTrigger>
            <TabsTrigger value="cabinet">Cabinet</TabsTrigger>
            <TabsTrigger value="issues">
              Issues{machine.openIssueCount > 0 ? ` (${machine.openIssueCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="devices">Tablet</TabsTrigger>
            <TabsTrigger value="cameras">Cameras</TabsTrigger>
          </TabsList>
          <div className="mt-4">
            <TabsContent value="pickups">
              <PickupsTab machineId={machineId} />
            </TabsContent>
            <TabsContent value="cabinet">
              <CabinetSettingsTab machine={machine} />
            </TabsContent>
            <TabsContent value="issues">
              <IssuesTab machineId={machineId} />
            </TabsContent>
            <TabsContent value="devices">
              <DevicesTab machine={machine} />
            </TabsContent>
            <TabsContent value="cameras">
              <MachineCamerasTab machine={machine} />
            </TabsContent>
          </div>
        </Tabs>
      ) : (
        <Tabs defaultValue="slots">
          <TabsList>
            <TabsTrigger value="slots">Stock</TabsTrigger>
            <TabsTrigger value="vends">Vends</TabsTrigger>
            <TabsTrigger value="issues">
              Issues{machine.openIssueCount > 0 ? ` (${machine.openIssueCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="cameras">Cameras</TabsTrigger>
          </TabsList>
          <div className="mt-4">
            <TabsContent value="slots">
              <SlotsTab machineId={machineId} locationId={machine.locationId} />
            </TabsContent>
            <TabsContent value="vends">
              <VendsTab machineId={machineId} />
            </TabsContent>
            <TabsContent value="issues">
              <IssuesTab machineId={machineId} />
            </TabsContent>
            <TabsContent value="devices">
              <DevicesTab machine={machine} />
            </TabsContent>
            <TabsContent value="cameras">
              <MachineCamerasTab machine={machine} />
            </TabsContent>
          </div>
        </Tabs>
      )}
    </Page>
  )
}
